import authService from './authService';
import {
  CardChargebackEntry,
  CieloNotificationLogEntry,
  CieloWebhookSearchModel,
} from '../types/cieloWebhook';
import { PaginatedResponse } from '../types/common';

/**
 * Consulta do webhook da Cielo. Somente leitura: quem escreve estas tabelas é o
 * PaymentHubGo — a tela existe para responder "o que a Cielo mandou e o que o
 * hub fez com aquilo", que é a única prova disponível no caso de chargeback
 * (evento que não tem ambiente de teste na Cielo).
 */
class CieloWebhookService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private buildParams(searchModel: CieloWebhookSearchModel): URLSearchParams {
    return new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.dateFrom && { date_from: searchModel.dateFrom }),
      ...(searchModel.dateTo && { date_to: searchModel.dateTo }),
      ...(searchModel.changeType !== undefined && { change_type: searchModel.changeType.toString() }),
      ...(searchModel.outcome !== undefined && { outcome: searchModel.outcome.toString() }),
      ...(searchModel.paymentId && { payment_id: searchModel.paymentId }),
    });
  }

  private toPaginated<T>(json: any, searchModel: CieloWebhookSearchModel): PaginatedResponse<T> {
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};
    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getNotifications(
    searchModel: CieloWebhookSearchModel,
  ): Promise<PaginatedResponse<CieloNotificationLogEntry>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/cielo-notifications?${this.buildParams(searchModel)}`);
    if (!response.ok) throw new Error('Falha ao buscar as notificações da Cielo');
    return this.toPaginated<CieloNotificationLogEntry>(await response.json(), searchModel);
  }

  async getChargebacks(
    searchModel: CieloWebhookSearchModel,
  ): Promise<PaginatedResponse<CardChargebackEntry>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/financial/cielo-chargebacks?${this.buildParams(searchModel)}`);
    if (!response.ok) throw new Error('Falha ao buscar os chargebacks');
    return this.toPaginated<CardChargebackEntry>(await response.json(), searchModel);
  }
}

export const cieloWebhookService = new CieloWebhookService();
